import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
} from "@material-ui/core";
import clsx from "clsx";
import DeleteIcon from "@material-ui/icons/Delete";
import { useStyles } from "./NoteItem.styles";
import useNotesContext from "../../../hooks/useNotesContext";
import useNotesPermissions from "../../../hooks/useNotesPermissions";
import DeleteConfirmDialog from "../DeleteConfirmDialog/DeleteConfirmDialog";
import useCurrentFacilityTimezone from "../../../hooks/useCurrentFacilityTimezone";
import { PinIcon } from "../PinIcon/PinIcon";

const MAX_LENGTH = 500;

const NoteItem = ({ note, onDeleteRequest }) => {
  const classes = useStyles();
  const { editNote, togglePin } = useNotesContext();
  const { canEdit, canDelete } = useNotesPermissions();
  const [content, setContent] = useState(note.noteText);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { convertUtcDateToFormat } = useCurrentFacilityTimezone();

  const displayTimestamp = note.updatedDate
    ? convertUtcDateToFormat(note.updatedDate, "MM/DD/YYYY hh:mm A")
    : note.createdDate
      ? convertUtcDateToFormat(note.createdDate, "MM/DD/YYYY hh:mm A")
      : "";

  const handleBlur = () => {
    setIsFocused(false);
    const trimmed = content.trim();
    if (trimmed.length === 0 || trimmed === note.noteText) {
      setContent(note.noteText);
      return;
    }
    editNote(note.noteID, trimmed);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    if (onDeleteRequest) {
      onDeleteRequest(note.noteID);
    }
  };

  return (
    <div className={clsx(classes.card, "note-item")}>
      <div className={classes.cardHeader}>
        <Typography variant="caption" className="note-timestamp">
          {displayTimestamp}
        </Typography>
        <Box display="flex" alignItems="center">
          {canEdit && (
            <IconButton
              size="small"
              onClick={() => togglePin(note.noteID)}
              className={clsx(classes.pinButton, "pin-button")}
            >
              <PinIcon pinned={note.pinned} />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              size="small"
              onClick={() => setDeleteDialogOpen(true)}
              className={clsx(classes.deleteButton, "delete-button")}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </div>

      <div className={classes.cardContent}>
        <TextField
          multiline
          fullWidth
          variant="outlined"
          size="small"
          value={content}
          onChange={(e) => setContent(e.target.value.slice(0, MAX_LENGTH))}
          onBlur={handleBlur}
          onFocus={() => setIsFocused(true)}
          disabled={!canEdit}
          inputProps={{ maxLength: MAX_LENGTH, className: "note-text", "data-value": content, "name": content }}
        />
        {canEdit && isFocused && (
          <Typography variant="caption" align="right" display="block" className="note-char-counter">
            {content.trim().length}/{MAX_LENGTH}
          </Typography>
        )}
      </div>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </div>
  );
};

export default NoteItem;
